import { useEffect, useState, useSyncExternalStore, useCallback } from "react";
import { trackEvent } from "@/lib/audit";

/**
 * PR-17 — Mobile Floating Control System.
 *
 * Central signals that drive auto-hide of every floating overlay on mobile.
 * Sources:
 *   - modal stack count (rewards, help, hub, voucher, poll detail, ai panel)
 *   - chat input focus (Safe Input Zone)
 *   - screen-clean mode (user-triggered "Limpiar Pantalla" button)
 *   - per-control hidden / collapsed state (dock persistence)
 *
 * Existing signals (goal clean mode, keyboard open, focus-mode prefs,
 * danger play mode) are still respected via mobileOverlayManager.
 */

export type FloatingControlId =
  | "halftime_fab"
  | "participate_fab"
  | "help_fab"
  | "rewards_fab"
  | "notifications_fab"
  | "retention_fab"
  | "share_fab"
  | "focus_fab"
  | "promo_banner";

export type FloatingControlState = "expanded" | "collapsed" | "hidden";

/**
 * PR-36 — FAB Discipline.
 * Lower index = higher priority. On mobile portrait, only the highest-priority
 * currently-mounted FAB is allowed to render. Everything else is suppressed
 * to eliminate clutter and visual fatigue during World Cup matches.
 * Order: Match-critical → Polls → Heat → Utility → Secondary.
 */
export const FAB_PRIORITY_ORDER: FloatingControlId[] = [
  "halftime_fab",      // match-critical
  "participate_fab",   // polls / engagement
  "retention_fab",     // heat / re-engage
  "help_fab",          // utility
  "share_fab",         // utility
  "focus_fab",         // utility (clean screen)
  "rewards_fab",       // secondary
  "notifications_fab", // secondary
  "promo_banner",      // secondary
];

const DOCK_KEY = "mobile_floating_state";
const CLEAN_KEY = "screen_clean_mode";
const SETTINGS_KEY = "mobile_layout_settings_v1";

export interface MobileLayoutSettings {
  enableFloatingDock: boolean;
  enableScreenCleanMode: boolean;
  autoHideOnTyping: boolean;
  autoHideOnModal: boolean;
  minimumBottomOffset: number; // px
  restoreDelayMs: number;
}

const DEFAULT_SETTINGS: MobileLayoutSettings = {
  enableFloatingDock: true,
  enableScreenCleanMode: true,
  autoHideOnTyping: true,
  autoHideOnModal: true,
  minimumBottomOffset: 120,
  restoreDelayMs: 250,
};

interface DockState { [id: string]: FloatingControlState }

function readDock(): DockState {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(window.localStorage.getItem(DOCK_KEY) || "{}"); } catch { return {}; }
}
function writeDock(d: DockState) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DOCK_KEY, JSON.stringify(d));
}

function readSettings(): MobileLayoutSettings {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try { return { ...DEFAULT_SETTINGS, ...JSON.parse(window.localStorage.getItem(SETTINGS_KEY) || "{}") }; }
  catch { return DEFAULT_SETTINGS; }
}
function writeSettings(s: MobileLayoutSettings) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}

// ----- store -----

interface Store {
  modalCount: number;
  inputFocused: boolean;
  screenClean: boolean;
  dock: DockState;
  settings: MobileLayoutSettings;
  mounted: Record<string, number>; // id → refcount of mounted requesters
}

const listeners = new Set<() => void>();
let state: Store = {
  modalCount: 0,
  inputFocused: false,
  screenClean: typeof window !== "undefined" && window.localStorage.getItem(CLEAN_KEY) === "1",
  dock: readDock(),
  settings: readSettings(),
  mounted: {},
};

function emit() { listeners.forEach((l) => l()); }
function subscribe(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }
function getSnapshot() { return state; }
function getServerSnapshot() { return state; }

// ----- modal stack -----

export function pushModal(name: string) {
  state = { ...state, modalCount: state.modalCount + 1 };
  void trackEvent("modal_priority_hide_executed", { modal: name, count: state.modalCount });
  emit();
}
export function popModal(name: string) {
  state = { ...state, modalCount: Math.max(0, state.modalCount - 1) };
  emit();
  void trackEvent("floating_control_restored", { source: "modal_close", modal: name });
}

/** Hook: register a modal's open state. */
export function useModalRegistration(open: boolean, name: string) {
  useEffect(() => {
    if (!open) return;
    pushModal(name);
    return () => popModal(name);
  }, [open, name]);
}

// ----- chat input focus -----

export function setInputFocus(focused: boolean) {
  if (state.inputFocused === focused) return;
  state = { ...state, inputFocused: focused };
  emit();
  void trackEvent(focused ? "mobile_safe_input_activated" : "mobile_safe_input_restored", {});
}

// ----- screen clean mode -----

export function toggleScreenCleanMode() {
  const next = !state.screenClean;
  state = { ...state, screenClean: next };
  if (typeof window !== "undefined") window.localStorage.setItem(CLEAN_KEY, next ? "1" : "0");
  void trackEvent(next ? "screen_clean_mode_enabled" : "screen_clean_mode_disabled", {});
  emit();
}

// ----- dock persistence -----

export function setControlState(id: FloatingControlId, s: FloatingControlState) {
  const dock = { ...state.dock, [id]: s };
  state = { ...state, dock };
  writeDock(dock);
  void trackEvent(s === "hidden" ? "floating_control_hidden" : "floating_control_restored", { id, state: s });
  emit();
}

// ----- settings (admin) -----

export function updateMobileLayoutSettings(patch: Partial<MobileLayoutSettings>) {
  const settings = { ...state.settings, ...patch };
  state = { ...state, settings };
  writeSettings(settings);
  emit();
}

// ----- FAB mount tracking (PR-36 priority discipline) -----

function registerMounted(id: FloatingControlId) {
  const mounted = { ...state.mounted, [id]: (state.mounted[id] ?? 0) + 1 };
  state = { ...state, mounted };
  emit();
}
function unregisterMounted(id: FloatingControlId) {
  const next = (state.mounted[id] ?? 0) - 1;
  const mounted = { ...state.mounted };
  if (next <= 0) delete mounted[id];
  else mounted[id] = next;
  state = { ...state, mounted };
  emit();
}

function isMobilePortrait(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 640 && window.innerHeight >= window.innerWidth;
}

/** Resolve the highest-priority mounted FAB that is not dock-hidden. */
function topPriorityVisible(s: Store): FloatingControlId | null {
  for (const id of FAB_PRIORITY_ORDER) {
    if (s.mounted[id] && s.dock[id] !== "hidden") return id;
  }
  return null;
}

// ----- hooks -----

export function useMobileFloatingStore() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Returns true if a floating control should be visible right now, taking
 * into account modals, input focus, screen-clean mode, and dock state.
 */
export function useFloatingControlVisible(id: FloatingControlId): boolean {
  const s = useMobileFloatingStore();
  // Register this FAB as mounted so the priority resolver can consider it.
  useEffect(() => {
    registerMounted(id);
    return () => unregisterMounted(id);
  }, [id]);
  // Re-emit on viewport changes so portrait/landscape transitions re-resolve.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => emit();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  if (!s.settings.enableFloatingDock) return true;
  if (s.dock[id] === "hidden") return false;
  if (s.screenClean) return false;
  if (s.settings.autoHideOnModal && s.modalCount > 0) return false;
  if (s.settings.autoHideOnTyping && s.inputFocused) return false;
  // PR-36 — Mobile portrait: only the single highest-priority FAB shows.
  if (isMobilePortrait()) {
    const top = topPriorityVisible(s);
    if (top && top !== id) return false;
  }
  return true;
}

export function useScreenCleanMode() {
  const s = useMobileFloatingStore();
  return s.screenClean;
}

export function useMobileLayoutSettings() {
  const s = useMobileFloatingStore();
  return s.settings;
}

/**
 * Hook helper for components that own a textarea/input: registers focus
 * and emits the audit pair. Returns onFocus/onBlur handlers.
 */
export function useSafeInputHandlers() {
  const onFocus = useCallback(() => setInputFocus(true), []);
  const onBlur = useCallback(() => setInputFocus(false), []);
  useEffect(() => () => setInputFocus(false), []);
  return { onFocus, onBlur };
}

/** Backwards-friendly local hook for components that need a per-control toggle UI. */
export function useControlState(id: FloatingControlId): [FloatingControlState, (s: FloatingControlState) => void] {
  const s = useMobileFloatingStore();
  const cur = s.dock[id] || "expanded";
  const set = useCallback((next: FloatingControlState) => setControlState(id, next), [id]);
  return [cur, set];
}

// Re-export for non-hook consumers
export function getMobileFloatingState() { return state; }

// Helper used by some legacy code
export function useFloatingDockReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => { setReady(true); }, []);
  return ready;
}